import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  Grid2x2,
  BarChart3,
  Users,
  MonitorSmartphone,
  Boxes,
  ShoppingCart,
  Package,
  Settings,
  ChevronDown,
  PanelLeftClose,
  GraduationCap,
  Wine,
  ShoppingBag,
  ClipboardList,
  Utensils,
  UtensilsCrossed,
  QrCode,
  Award
} from 'lucide-react';

/* Resizable sidebar width, remembered per device, so long menu labels needn't be squinted at. */
const MIN_WIDTH = 180;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 256;
const WIDTH_KEY = 'restoboxx.sidebarWidth';

const clampWidth = (n) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(n)));

/** The remembered width, ignoring anything stale or out of range. */
const storedWidth = () => {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(n) && n >= MIN_WIDTH && n <= MAX_WIDTH) return n;
  } catch (e) { /* private mode / disabled storage — use the default */ }
  return DEFAULT_WIDTH;
};

/* The whole navigation tree. Exported because the header's menu search walks
   the SAME structure — a second list of what this app contains would drift
   from this one the first time a page moved. */
export function buildSections(restaurantId, t) {
  const withId = (path, fallback) => (restaurantId ? path.replace(':restaurantId', restaurantId) : fallback);
  return [
    {
      title: t('nav.overview'),
      items: [
        /* disabled: TEMPORARILY parked (product call) — shown greyed, clicks do
           nothing, no message. Remove the flag to bring one back. */
        { label: t('nav.dashboard'), to: '/dashboard', icon: Grid2x2, perm: 'dashboard.view', disabled: true },
        {
          label: 'Revenue & Sales Reports', icon: BarChart3, perm: 'dashboard.view', disabled: true, children: [
            { label: 'All Reports', to: '/reports' },
            { label: 'Sales & Revenue', to: '/reports/sales' },
            { label: 'Menu Performance', to: '/reports/menu' },
            { label: 'Service & Consistency', to: '/reports/service' },
          ]
        },
        {
          // Family of reservation reports — one entry per report/route.
          label: 'Reservation Insights', icon: BarChart3, perm: 'dashboard.view', disabled: true, children: [
            // end: exact match, else Overview also lights up on sub-reports.
            { label: 'Overview',                 to: '/reservations/insights', end: true },
            { label: 'Cancellations & No-shows', to: '/reservations/insights/cancellations' },
            { label: 'Booking Funnel',           to: '/reservations/insights/funnel' },
            { label: 'No-show by Lead Time',     to: '/reservations/insights/lead-time' },
            { label: 'Demand Heatmap',           to: '/reservations/insights/heatmap' },
            { label: 'Channel Economics',        to: '/reservations/insights/economics' },
          ],
        },
      ],
    },
    {
      title: 'Staff',
      items: [
        {
          label: t('nav.staff'), icon: Users, perm: 'staff.view', children: [
            { label: t('nav.allStaff'), to: '/staff-management' },
            { label: 'Roles', to: '/roles-management' },
            // Active staff per location + their login/session info.
            { label: t('nav.userSessions'), to: '/user-sessions' },
          ]
        },
      ],
    },
    {
      title: 'Inventory',
      items: [
        {
          label: 'Inventory', icon: Boxes, perm: 'inventory.view', children: [
            { label: t('nav.itemTracking'), icon: Package, to: withId('/restaurant/:restaurantId/inventory', '/inventory') },
            { label: t('nav.purchase'), to: withId('/restaurant/:restaurantId/purchase', '/purchase'), icon: ShoppingBag },
            { label: 'Food Cost Report', to: '/reports/food-cost', icon: BarChart3 },
            { label: t('nav.barInventory'), icon: Wine, to: withId('/restaurant/:restaurantId/bar-inventory', '/bar-inventory') },
            {
              label: t('nav.liquorManagement'), icon: BarChart3, children: [
                { label: t('nav.liquorConsumption'), to: withId('/restaurant/:restaurantId/liquor-management?tab=consumption', '/liquor-management?tab=consumption') },
                { label: t('nav.liquorStock'), to: withId('/restaurant/:restaurantId/liquor-management?tab=stock', '/liquor-management?tab=stock') },
                { label: t('nav.liquorTopSelling'), to: withId('/restaurant/:restaurantId/liquor-management?tab=top', '/liquor-management?tab=top') },
              ]
            },
            { label: t('nav.liquorTracking'), icon: ClipboardList, to: withId('/restaurant/:restaurantId/liquor-tracking', '/liquor-tracking') },
          ]
        },
      ],
    },
    {
      // Directly under Inventory: stock and the dishes cooked from it are read together.
      title: t('nav.menu'),
      items: [
        {
          label: 'Menu & Dishes', icon: Boxes, children: [
            { label: t('nav.allMenus'), to: withId('/restaurant/:restaurantId/menus', '/menu-management'), perm: 'menus.view' },
            // Dishes stay visible to kitchen roles: the View dialog carries the ingredients and recipe they work from.
            { label: t('nav.allDishes'), to: withId('/restaurant/:restaurantId/dishes', '/dishes'), perm: 'dishes.view' },
          ]
        },
      ],
    },
    {
      title: 'Tables',
      items: [
        {
          label: 'Tables', icon: MonitorSmartphone, perm: 'tables.view', children: [
            // Live floor view — which tables are occupied / free right now.
            { label: t('nav.viewTables'), to: withId('/restaurant/:restaurantId/tables', '/tables') },
            /* QR Codes moved to the Self Service Orders module below —
               the QR is the guest's door into self-ordering, not a floor fact. */
          ]
        },
      ],
    },
    {
      // Guests ordering from their own phones: the QR that gets them in, and
      // how that flow pays — its own module, since a counter running on QR
      // orders thinks of this as one thing, not as a corner of Tables.
      title: 'Self Service',
      items: [
        {
          label: 'Self Service Orders', icon: QrCode, children: [
            { label: 'QR Codes', to: withId('/restaurant/:restaurantId/qr-codes', '/qr-codes'), perm: 'tables.view' },
            /* Kiosk Settings moved to the Settings section below (owner,
               2026-11-01): a kiosk is a machine to set up, like Devices. */
            /* Kiosk analytics. Lives here rather than under the generic
               Reports section because a counter reads speed, peaks and
               channel mix as part of running self-service, not as reporting. */
            {
              label: 'Insights', perm: 'dashboard.view', children: [
                // end: exact match, else Overview lights up on every report.
                { label: 'Overview', to: '/self-service/insights', end: true },
                { label: t('insights.report.festivals.title'), to: '/self-service/insights/festivals' },
                { label: t('insights.report.speed.title'), to: '/self-service/insights/speed' },
                { label: t('insights.report.demand.title'), to: '/self-service/insights/demand' },
                { label: t('insights.report.channels.title'), to: '/self-service/insights/channels' },
                { label: t('insights.report.payments.title'), to: '/self-service/insights/payments' },
                { label: t('insights.report.accuracy.title'), to: '/self-service/insights/accuracy' },
              ],
            },
          ],
        },
      ],
    },
    {
      // Reservations - the future-bookings ledger, booking form and policy, in its own section below Tables.
      title: t('nav.reservations'),
      items: [
        {
          label: t('nav.reservations'), icon: ClipboardList, perm: 'tables.view', children: [
            { label: 'All Reservations', to: withId('/restaurant/:restaurantId/reservations/all', '/reservations/all') },
            // end: exact match, else this also lights up on /reservations/all.
            { label: 'Create New Reservation', to: withId('/restaurant/:restaurantId/reservations', '/reservations'), end: true },
            /* The public booking link and its QR — for a website, an
               Instagram bio or a card at the desk. Moved here from QR Codes:
               those are doors into ordering, this is a door into booking. */
            { label: 'Reservation Link', to: withId('/restaurant/:restaurantId/reservations/link', '/reservations/link') },
            { label: 'Reservation Settings', to: '/settings/reservations', perm: 'settings.view' },
          ],
        },
      ],
    },
    {
      // Buffet management — its own section so it's immediately visible.
      title: 'Buffet',
      items: [
        {
          label: 'Buffet', icon: UtensilsCrossed, children: [
            // end: exact match, else this also lights up on the sibling routes.
            /* The branch's buffets: this screen DEFINES them; which dates one runs on is Open Buffets. */
            { label: 'Buffet List', to: withId('/restaurant/:restaurantId/buffets', '/buffets'), end: true },
            /* The other half: which DATES a template runs on, and what any one of those days does differently. */
            { label: 'Open Buffets', to: withId('/restaurant/:restaurantId/buffets/open', '/buffets/open') },
            /* The station REGISTER (master data, same panel as Settings → Master Data →
               Live Counters) — offered here because the person defining a tandoor is
               the one building buffets. Needs the master_data permission to change. */
            { label: 'Live Counters', to: withId('/restaurant/:restaurantId/buffets/live-counters', '/buffets/live-counters'), perm: 'master_data.view' },
            /* The dish-portion REGISTER — the studio's old Quantity-per-item
               step, defined once here instead of per buffet. */
            { label: 'Dish Quantities', to: withId('/restaurant/:restaurantId/buffets/dish-quantities', '/buffets/dish-quantities'), perm: 'master_data.view' },
          ],
        },
        {
          // Buffet analytics — every insight is its own report page.
          label: 'Open Buffet Insights', icon: BarChart3, children: [
            { label: 'Overview', to: '/buffets/insights', end: true },
            { label: 'Weekly Covers', to: '/buffets/insights/covers' },
            { label: 'Food Cost vs Target', to: '/buffets/insights/food-cost' },
            { label: 'Wastage Heatmap', to: '/buffets/insights/wastage' },
            { label: 'Uptake Accuracy', to: '/buffets/insights/uptake' },
            { label: 'Dish Economics', to: '/buffets/insights/economics' },
            { label: 'Bookings by Buffet', to: '/buffets/insights/bookings-buffet' },
            { label: 'Classic Buffet Report', to: '/reports/buffet' },
          ],
        },
      ],
    },
    {
      title: t('nav.orders'),
      items: [
        {
          label: t('nav.orders'), icon: ShoppingCart, children: [
            { label: t('nav.manageOrders'), to: '/order', perm: 'orders.manage' },
            { label: 'Active Orders', to: '/orders/active', perm: 'orders.manage' },
            { label: 'Kitchen View', to: '/kitchen', perm: 'orders.view' },
            { label: 'All Orders', to: '/orders/all', perm: 'orders.manage' },
          ]
        },
      ],
    },
    {
      title: 'Customers',
      items: [
        {
          label: 'Customers', icon: Users, perm: 'customers.view', children: [
            { label: 'All Customers', to: withId('/restaurant/:restaurantId/customers', '/customers') },
            { label: 'CRM (Customer 360)', to: '/crm' },
            { label: 'Guest Segments (RFM)', to: '/reports/crm-segments' },
            { label: 'Top Customers', to: '/reports/crm-top-customers' },
            { label: 'Retention', to: '/reports/crm-retention' },
            { label: 'Menu Magnets', to: '/reports/crm-menu-magnets' },
            { label: 'Win-back List', to: '/reports/crm-winback' },
          ]
        },
      ],
    },
    {
      // The retention half of Customers: the programme, its members, what
      // points buy, and the gift cards sold beside it.
      title: t('loyalty.title'),
      items: [
        {
          label: t('loyalty.title'), icon: Award, perm: 'loyalty.view', children: [
            { label: t('loyalty.manager'), to: '/loyalty', end: true },
            { label: t('loyalty.members'), to: '/loyalty/members' },
            { label: t('loyalty.rewards'), to: '/loyalty/rewards' },
            { label: t('loyalty.giftCards'), to: '/loyalty/gift-cards', perm: 'gift_cards.view' },
            { label: t('loyalty.settings'), to: '/settings/loyalty', perm: 'loyalty.manage' },
          ]
        },
      ],
    },
    {
      title: t('nav.settings'),
      items: [
        {
          label: t('nav.settings'), icon: Settings, children: [
            { label: t('nav.companyProfile'), to: '/settings/company',     perm: 'company.view' },
            // Franchise-arrangement register beside the groups below.
            { label: t('org.nav'),            to: '/settings/organisation', perm: 'company.view' },
            // Company -> Restaurant Group (franchise / brand) -> Location; shares the Locations permission.
            { label: t('nav.restaurantGroups'), to: '/settings/restaurant-groups', perm: 'locations.view' },
            { label: t('nav.locations'),      to: '/settings/locations',   perm: 'locations.view' },
            {
              /* Each website concern is its own entry - the page's old inner tabs, promoted to addresses. */
              label: 'Website Settings', children: [
                { label: 'Traffic',      to: '/settings/website/traffic',      perm: 'settings.view' },
                { label: 'Photos',       to: '/settings/website/photos',       perm: 'settings.view' },
                { label: 'Menus',        to: '/settings/website/menus',        perm: 'settings.view' },
                { label: 'Reservations', to: '/settings/website/reservations', perm: 'settings.view' },
                { label: 'Site',         to: '/settings/website/site',         perm: 'settings.view' },
                /* What a guest can type into the site's Near-me table finder: hotels, landmarks, the Popular six. */
                { label: t('nav.finderPlaces'), to: '/settings/website/places', perm: 'settings.view' },
              ],
            },
            { label: t('nav.dataDisplay'),    to: '/settings/preferences', perm: 'data_display.view' },
            { label: t('nav.masterData'),     to: '/settings/masters',     perm: 'master_data.view' },
            {
              label: 'Tax', children: [
                { label: 'Tax Rates',     to: '/settings/tax-rates',     perm: 'settings.view' },
                { label: 'Tax Groups',    to: '/settings/tax-groups',    perm: 'settings.view' },
                { label: 'Tax Migration', to: '/settings/tax-migration', perm: 'settings.view' },
              ],
            },
            {
              /* Everything that sends a guest a message: the three channels, and
                 the per-event templates they carry. */
              label: 'Notifications', children: [
                { label: 'SMS',       to: '/settings/notifications/sms',       perm: 'settings.view' },
                { label: 'Email',     to: '/settings/notifications/email',     perm: 'settings.view' },
                { label: 'WhatsApp',  to: '/settings/notifications/whatsapp',  perm: 'settings.view' },
                { label: 'Templates', to: '/settings/notifications/templates', perm: 'settings.view' },
              ],
            },
            /* The trading calendar: public holidays and festivals, and the
               custom events a branch adds. One store, read by the buffet run
               calendar and by the kiosk insights reports. */
            { label: 'Calendar',              to: '/settings/calendar',     perm: 'settings.view' },
            { label: 'Menu View',             to: '/settings/menu-view',    perm: 'settings.view' },
            { label: t('nav.display'),        to: '/settings/display',      perm: 'settings.view' },
            { label: 'Realtime',              to: '/settings/realtime',     perm: 'settings.view' },
            // The POS terminals / phones approved to run on this restaurant.
            { label: 'Devices',               to: '/settings/devices',      perm: 'devices.view' },
            /* The counter's own terminals: which kiosks exist per branch,
               whether each is live, what they play while idle and how long
               they wait before clearing an abandoned order. */
            { label: t('nav.kioskSettings'),  to: '/settings/kiosk',        perm: 'settings.view' },
          ]
        },
        // RestoBOXX-InTutor — the in-app manual; visible to every user.
        { label: 'RestoBOXX-InTutor', to: '/help', icon: GraduationCap },
      ],
    },
  ];
}

/* Who may SEE a nav item — one rule, shared with the header's menu search so
   the two can never disagree about what a user can reach. Built as a factory
   because the answer needs the signed-in user, and callers hold that already.
   Super admin is the 'Super Admin' role (matching backend scoping) or a
   central accounts_user superuser. */
export function makeIsItemAllowed({ role, user, hasPermission, isPermissionGated }) {
  const isSuperAdmin = role?.name === 'Super Admin' || !!user?.is_superuser;
  // An item hides when it is super-admin-only without one, or when its gate is opted in and the user lacks it.
  const allowed = (item) => {
    if (item?.superAdminOnly && !isSuperAdmin) return false;
    if (item?.perm && isPermissionGated(item.perm) && !hasPermission(item.perm)) return false;
    // A dropdown whose children are all hidden disappears too, rather than showing an empty group.
    if (Array.isArray(item?.children) && item.children.length > 0) {
      return item.children.some(allowed);
    }
    return true;
  };
  return allowed;
}

export default function Sidebar({ onClose, onOpen, collapsed = false }) {
  const { restaurantId } = useRestaurant();
  const { pathname } = useLocation();
  const { t } = useT();
  const { hasPermission, isPermissionGated, role, user } = useAuth();
  const [open, setOpen] = useState({});
  const [width, setWidth] = useState(storedWidth);
  const [dragging, setDragging] = useState(false);

  /* Written on release, not on every mouse move: a drag is one decision and one entry in storage. */
  const remember = useCallback((w) => {
    try { localStorage.setItem(WIDTH_KEY, String(w)); } catch (e) { /* no storage */ }
  }, []);

  const setAndRemember = useCallback((w) => {
    const next = clampWidth(w);
    setWidth(next);
    remember(next);
  }, [remember]);

  /* The drag captures the pointer, so releasing outside the browser still ends the resize. */
  const startDrag = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;   // left button / touch only
    e.preventDefault();
    const handle = e.currentTarget;
    const { pointerId } = e;
    const startX = e.clientX;
    const startW = width;
    setDragging(true);
    try { handle.setPointerCapture(pointerId); } catch (err) { /* unsupported */ }

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      setWidth(clampWidth(startW + (ev.clientX - startX)));
    };
    const onUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      try { handle.releasePointerCapture(pointerId); } catch (err) { /* already gone */ }
      setDragging(false);
      setAndRemember(startW + (ev.clientX - startX));
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }, [width, setAndRemember]);

  /* Mid-drag the pointer is over the page, so the resize cursor and no-select guard go on the document. */
  useEffect(() => {
    if (!dragging) return undefined;
    const { style } = document.body;
    const prevCursor = style.cursor;
    const prevSelect = style.userSelect;
    style.cursor = 'col-resize';
    style.userSelect = 'none';
    return () => { style.cursor = prevCursor; style.userSelect = prevSelect; };
  }, [dragging]);

  /* The same resize from the keyboard: the handle is a focusable separator, so arrows nudge and Home/End jump. */
  const onHandleKeyDown = (e) => {
    const step = e.shiftKey ? 48 : 16;
    if (e.key === 'ArrowLeft') { e.preventDefault(); setAndRemember(width - step); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setAndRemember(width + step); }
    else if (e.key === 'Home') { e.preventDefault(); setAndRemember(MIN_WIDTH); }
    else if (e.key === 'End') { e.preventDefault(); setAndRemember(MAX_WIDTH); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAndRemember(DEFAULT_WIDTH); }
  };

  const isItemAllowed = makeIsItemAllowed({ role, user, hasPermission, isPermissionGated });

  // Render a nav item recursively so a dropdown can hold links AND further dropdowns; keyPath keeps state keys unique.
  const renderItem = (item, depth = 0, keyPath = '') => {
    const Icon = item.icon;
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const key = `${keyPath}/${item.label}`;
    const isOpen = open[key];
    /* Parked entry: kept in place so the menu's shape doesn't shift, but inert —
       greyed, no navigation, no unfold, and deliberately no explanation. */
    if (item.disabled) {
      return (
        <div
          key={key}
          aria-disabled="true"
          className="flex items-center justify-between px-3 py-2 rounded-md text-sm text-muted-foreground/50 cursor-default select-none"
        >
          <span className="flex min-w-0 flex-1 items-center gap-2" title={item.label}>
            {Icon && <Icon size={16} className="shrink-0 opacity-50" />}
            <span className="truncate">{item.label}</span>
          </span>
          {hasChildren && <ChevronDown size={16} className="shrink-0 opacity-50" />}
        </div>
      );
    }
    if (hasChildren) {
      return (
        <div key={key}>
          <button
            type="button"
            onClick={() => setOpen((prev) => ({ ...prev, [key]: !prev[key] }))}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
              isOpen ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'
            }`}
          >
            {/* min-w-0 + truncate: dragged narrow, a long label ellipses instead of wrapping and breaking the row rhythm. */}
            <span className="flex min-w-0 flex-1 items-center gap-2" title={item.label}>
              {Icon && <Icon size={16} className="shrink-0" />}
              <span className="truncate">{item.label}</span>
            </span>
            <ChevronDown size={16} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div className="mt-1 ml-4 space-y-1">
              {item.children.filter(isItemAllowed).map((child) => renderItem(child, depth + 1, key))}
            </div>
          )}
        </div>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.to || '/'}
        end={item.end}
        className={({ isActive }) =>
          `flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
            isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
          }`
        }
      >
        <span className="flex min-w-0 items-center gap-2" title={item.label}>
          {Icon && <Icon size={16} className="shrink-0" />}
          <span className="truncate">{item.label}</span>
        </span>
      </NavLink>
    );
  };

  /* Collapsed, the sidebar keeps its icons rather than leaving the edge blank:
     a rail of the top-level entries, each one still a target. A leaf navigates;
     a group cannot show its children in 64px, so it opens the drawer with that
     group already unfolded — the click means the same thing either way. */
  const isBranchActive = (item) => {
    const routes = [];
    const walk = (it) => {
      if (it.to) routes.push(it.to);
      (it.children || []).forEach(walk);
    };
    walk(item);
    return routes.some((to) => pathname === to || pathname.startsWith(`${to}/`));
  };

  const railClass = (active) =>
    `flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors ${
      active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
    }`;

  const renderRailItem = (item, sectionTitle) => {
    const Icon = item.icon;
    const key = `${sectionTitle}/${item.label}`;
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    // The title carries the label the rail has no room to print.
    if (item.disabled) {
      return (
        <span
          key={key}
          aria-disabled="true"
          title={item.label}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground/50"
        >
          {Icon && <Icon size={18} />}
        </span>
      );
    }
    if (hasChildren) {
      return (
        <button
          key={key}
          type="button"
          title={item.label}
          aria-label={item.label}
          onClick={() => {
            setOpen((prev) => ({ ...prev, [key]: true }));
            if (onOpen) onOpen();
          }}
          className={railClass(isBranchActive(item))}
        >
          {Icon && <Icon size={18} />}
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.to || '/'}
        end={item.end}
        title={item.label}
        aria-label={item.label}
        className={({ isActive }) => railClass(isActive)}
      >
        {Icon && <Icon size={18} />}
      </NavLink>
    );
  };

  if (collapsed) {
    const railSections = buildSections(restaurantId, t)
      .map((section) => ({ ...section, items: section.items.filter(isItemAllowed) }))
      .filter((section) => section.items.length > 0);
    return (
      <div className="relative h-full w-16 shrink-0 border-r bg-background flex flex-col">
        {/* Same h-14 brand bar as the open sidebar, so the two header borders still line up. */}
        <div className="h-14 shrink-0 border-b bg-card flex items-center justify-center">
          <button
            type="button"
            onClick={onOpen}
            aria-label={t('common.showSidebar')}
            title={t('common.showSidebar')}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Utensils size={16} strokeWidth={2.4} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden py-3 flex flex-col items-center gap-1">
          {railSections.map((section, idx) => (
            <React.Fragment key={section.title}>
              {/* The group headings have no room here, so a hairline stands in for them. */}
              {idx > 0 && <span className="my-2 h-px w-8 bg-border" aria-hidden="true" />}
              {section.items.map((item) => renderRailItem(item, section.title))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ width }}
      /* shrink-0 stops the flex row squeezing the sidebar back; the transition is dropped while dragging. */
      className={`relative h-full shrink-0 border-r bg-background flex flex-col ${
        dragging ? '' : 'transition-[width] duration-150 ease-out'
      }`}
    >
      {/* Brand — height matches the main top header so the borders align */}
      <div className="pl-5 pr-3 h-14 shrink-0 border-b bg-card flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          {/* The brand tile: a glossy BLACK squircle — lit at the top, deep
              black at the foot — with the bold white fork & knife. Colors are
              deliberately literal (not theme tokens): the mark is the mark,
              in either theme. */}
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-gradient-to-b from-zinc-500 via-zinc-900 to-black text-white shadow-sm">
            <Utensils size={18} strokeWidth={2.7} />
          </span>
          <span className="brand-wordmark brand-wordmark-caps brand-wordmark-bar truncate text-base text-primary">RestoBoxx</span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide sidebar"
            title="Hide sidebar"
            className="p-1 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
          >
            <PanelLeftClose size={18} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        {buildSections(restaurantId, t).map((section, idx) => {
          const visibleItems = section.items.filter(isItemAllowed);
          if (visibleItems.length === 0) return null;
          return (
          <div key={idx} className="mb-4">
            <div className="text-xs text-slate-500 uppercase mb-2 px-2">{section.title}</div>
            <div className="space-y-1">
              {visibleItems.map((item) => renderItem(item, 0, section.title))}
            </div>
          </div>
          );
        })}
      </div>

      {/* Drag handle: a 6px hit strip straddling the edge, with only a 2px line inside it lighting up. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        onPointerDown={startDrag}
        onDoubleClick={() => setAndRemember(DEFAULT_WIDTH)}
        onKeyDown={onHandleKeyDown}
        className="group absolute inset-y-0 -right-[3px] z-20 w-[6px] cursor-col-resize touch-none focus:outline-none"
      >
        <div
          className={`h-full w-[2px] mx-auto rounded-full transition-colors ${
            dragging ? 'bg-primary' : 'bg-transparent group-hover:bg-primary/60 group-focus-visible:bg-primary'
          }`}
        />
      </div>
    </div>
  );
}
